<!-- Компактный визуальный этап одного титула внутри карточки ЭДО. -->
<template>
  <BaseTooltip
    class="edo-title-flow-step__tooltip"
    position="top"
    :max-width="260"
    variant="light"
    close-on-click
  >
    <button
      type="button"
      class="edo-title-flow-step"
      :class="[
        `edo-title-flow-step--${tone}`,
        {
          'edo-title-flow-step--selected': selected,
          'edo-title-flow-step--compact': compact,
        },
      ]"
      :aria-label="ariaLabel || label"
      :aria-pressed="selected"
      :disabled="disabled"
      @click="$emit('select')"
    >
      <span
        class="edo-title-flow-step__bar"
        aria-hidden="true"
      ></span>
      <span class="edo-title-flow-step__label">{{ label }}</span>
      <span v-if="note" class="edo-title-flow-step__note">
        {{ note }}
      </span>
    </button>

    <template #content>
      <span class="edo-title-flow-tooltip">
        <span class="edo-title-flow-tooltip__head">
          <strong>{{ tooltip.title || label }}</strong>
          <span
            v-if="tooltip.statusLabel"
            class="edo-title-flow-tooltip__status"
            :class="`edo-title-flow-tooltip__status--${
              tooltip.tone || tone
            }`"
          >
            {{ tooltip.statusLabel }}
          </span>
        </span>
        <span
          v-if="tooltip.participant"
          class="edo-title-flow-tooltip__participant"
        >
          {{ tooltip.participant }}
        </span>
        <span
          v-if="tooltip.description"
          class="edo-title-flow-tooltip__description"
        >
          {{ tooltip.description }}
        </span>
      </span>
    </template>
  </BaseTooltip>
</template>

<script>
export default {
  name: "EdoDocumentTitleFlowStep",

  emits: ["select"],

  props: {
    label: {
      type: String,
      required: true,
    },
    note: {
      type: String,
      default: "",
    },
    tone: {
      type: String,
      default: "neutral",
      validator: (value) =>
        ["neutral", "success", "warning", "danger"].includes(value),
    },
    selected: {
      type: Boolean,
      default: false,
    },
    ariaLabel: {
      type: String,
      default: "",
    },
    tooltip: {
      type: Object,
      default: () => ({}),
    },
    disabled: {
      type: Boolean,
      default: false,
    },
    compact: {
      type: Boolean,
      default: false,
    },
  },
};
</script>

<style scoped>
.edo-title-flow-step__tooltip {
  width: 100%;
}

.edo-title-flow-tooltip {
  display: flex;
  flex-direction: column;
  gap: 9px;
  width: 228px;
  max-width: 100%;
  max-height: min(280px, calc(100vh - 32px));
  overflow-y: auto;
  text-align: left;
}

.edo-title-flow-tooltip__head {
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.edo-title-flow-tooltip__head strong {
  color: #202430;
  font-size: 14px;
  font-weight: 700;
  line-height: 19px;
}

.edo-title-flow-tooltip__status {
  align-self: flex-start;
  padding: 3px 7px;
  border-radius: 999px;
  background: #f1f3f7;
  color: #5b6070;
  font-size: 11px;
  font-weight: 700;
  line-height: 15px;
}

.edo-title-flow-tooltip__status--success {
  background: #e7f7f0;
  color: #147653;
}

.edo-title-flow-tooltip__status--warning {
  background: #fff6df;
  color: #93630c;
}

.edo-title-flow-tooltip__status--danger {
  background: #fff0ef;
  color: #b43434;
}

.edo-title-flow-tooltip__participant {
  color: #303545;
  font-size: 12px;
  font-weight: 600;
  line-height: 17px;
}

.edo-title-flow-tooltip__description {
  padding-top: 9px;
  border-top: 1px solid #e9ebf0;
  color: #5b6070;
  font-size: 12px;
  font-weight: 400;
  line-height: 17px;
}

.edo-title-flow-step {
  display: flex;
  flex-direction: column;
  gap: 6px;
  min-width: 0;
  min-height: 44px;
  width: 100%;
  padding: 10px 12px 12px;
  border: 0;
  border-radius: 12px;
  background: #fff;
  color: inherit;
  font: inherit;
  text-align: left;
  cursor: pointer;
  transition: background-color 0.16s ease, box-shadow 0.16s ease;
}

.edo-title-flow-step:hover {
  background: #f7f8fb;
  box-shadow: inset 0 0 0 1px #e3e6ee;
}

.edo-title-flow-step:disabled {
  cursor: default;
}

.edo-title-flow-step:disabled:hover {
  background: #fff;
  box-shadow: none;
}

.edo-title-flow-step--compact,
.edo-title-flow-step--compact:hover,
.edo-title-flow-step--compact:disabled:hover {
  gap: 6px;
  min-height: 0;
  padding: 0;
  border-radius: 0;
  background: transparent;
  box-shadow: none;
}

.edo-title-flow-step--selected,
.edo-title-flow-step--selected:hover {
  background: #f3f5f9;
  box-shadow: inset 0 0 0 1.5px #c9ceda;
}

.edo-title-flow-step__bar {
  position: relative;
  overflow: hidden;
  height: 6px;
  border-radius: 999px;
  background: #e3e6ee;
}

.edo-title-flow-step__label {
  color: var(--color-dark);
  font-size: 13px;
  font-weight: 600;
  line-height: 18px;
}

.edo-title-flow-step__note {
  overflow: hidden;
  color: #5b6070;
  font-size: 12px;
  line-height: 17px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.edo-title-flow-step--success .edo-title-flow-step__bar {
  background: var(--color-green);
}

.edo-title-flow-step--warning .edo-title-flow-step__label,
.edo-title-flow-step--warning .edo-title-flow-step__note {
  color: #854d0e;
}

.edo-title-flow-step--warning .edo-title-flow-step__bar {
  background: #f59e0b;
}

.edo-title-flow-step--danger .edo-title-flow-step__bar {
  background: var(--color-danger);
}

.edo-title-flow-step--danger .edo-title-flow-step__note {
  color: var(--color-danger-hover);
}

.edo-title-flow-step--neutral .edo-title-flow-step__label,
.edo-title-flow-step--neutral .edo-title-flow-step__note {
  color: #5b6070;
}

.edo-title-flow-step--success.edo-title-flow-step--selected
  .edo-title-flow-step__bar::after,
.edo-title-flow-step--warning.edo-title-flow-step--selected
  .edo-title-flow-step__bar::after,
.edo-title-flow-step--danger.edo-title-flow-step--selected
  .edo-title-flow-step__bar::after {
  content: "";
  position: absolute;
  inset: 0;
  background: linear-gradient(
    100deg,
    transparent 25%,
    rgba(255, 255, 255, 0.38) 50%,
    transparent 75%
  );
  transform: translateX(-110%);
  animation: edo-title-flow-success 6s ease-in-out infinite;
}

.edo-title-flow-step:focus-visible {
  outline: 2px solid var(--color-blue);
  outline-offset: 4px;
}

@keyframes edo-title-flow-success {
  0%,
  55% {
    transform: translateX(-110%);
  }
  85%,
  100% {
    transform: translateX(110%);
  }
}

@media (prefers-reduced-motion: reduce) {
  .edo-title-flow-step--success .edo-title-flow-step__bar::after,
  .edo-title-flow-step--warning .edo-title-flow-step__bar::after,
  .edo-title-flow-step--danger .edo-title-flow-step__bar::after {
    animation: none;
  }
}

@media (max-width: 440px) {
  .edo-title-flow-step {
    gap: 5px;
    padding: 8px 8px 10px;
    border-radius: 10px;
  }
}
</style>
